'use client'

import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy, faFire, faBolt, faCaretUp, faCaretDown, faAward } from '@fortawesome/free-solid-svg-icons';
import './results-row.css';
import Avatar from './user-avatar';
import GlassCard from '@/components/shared/GlassCard';
import { trackEvent } from '@/components/utils/analytics';

const getFormMarkup = (lastFive) => {
  if (!lastFive) return [];
  return lastFive.split(',').map((result, index) => (
    <span
      key={index}
      className={`form-pip form-pip--${result.toLowerCase()}`}
      title={result.toUpperCase() === 'W' ? 'Win' : 'Loss'}
    />
  ));
};

const RankChange = ({ change }) => {
  if (change == null || change === 0) return null;
  const isUp = change > 0;
  return (
    <span className={`rank-change rank-change--${isUp ? 'up' : 'down'}`}>
      <FontAwesomeIcon icon={isUp ? faCaretUp : faCaretDown} />{Math.abs(change)}
    </span>
  );
};

const ResultsRow = ({
  name, won, loss, bonus, lastFive, accuracy, best,
  id, imageurl, rank, rankChange, animationDelayValue, totalPoints,
}) => {
  const router = useRouter();

  // Inner CTA hijacks the row's parent Link by stopping propagation. We
  // route programmatically so the prefetch still works without nesting <a>.
  const handleRecapClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    trackEvent('view_player_recap', { player_id: id, player_name: name, source: 'leaderboard' });
    router.push(`/recap/${id}`);
  };

  return (
    <Link href={`/user/${id}`} className="player-row-link animate__animated animate__fadeIn" style={{ animationDelay: `${animationDelayValue}s` }} onClick={() => trackEvent('view_player_profile', { player_id: id, player_name: name, source: 'leaderboard' })}>
      <GlassCard variant="subtle" className="player-row">
        {/* Rank watermark */}
        <span className={`player-row__rank-bg rank-bg--${rank}`}>{rank}</span>

        {/* Left: avatar + identity */}
        <div className="player-row__identity">
          <div className="player-row__avatar">
            <Avatar name={name} imageurl={imageurl} width={36} height={36} />
          </div>
          <div className="player-row__info">
            <span className="player-row__name">{name} <RankChange change={rankChange} /></span>
            <span className="player-row__form">{getFormMarkup(lastFive)}</span>
          </div>
        </div>

        {/* Center: key stats */}
        <div className="player-row__stats">
          <div className="player-row__stat">
            <span className="player-row__stat-value">{won ? parseFloat(won).toFixed(1) : 0}</span>
            <span className="player-row__stat-label"><FontAwesomeIcon icon={faTrophy} /> Won</span>
          </div>
          <div className="player-row__stat">
            <span className="player-row__stat-value player-row__stat-value--loss">{loss}</span>
            <span className="player-row__stat-label">Lost</span>
          </div>
          <div className="player-row__stat">
            <span className="player-row__stat-value player-row__stat-value--streak">{best || 0}</span>
            <span className="player-row__stat-label"><FontAwesomeIcon icon={faFire} /> Streak</span>
          </div>
          <div className="player-row__stat">
            <span className="player-row__stat-value player-row__stat-value--accuracy">{accuracy}%</span>
            <span className="player-row__stat-label"><FontAwesomeIcon icon={faBolt} /> Acc</span>
          </div>
        </div>

        {/* Right: points */}
        <div className="player-row__points">
          <div className="player-row__points-main">
            <span className="player-row__total">{totalPoints}</span>
            <span className="player-row__pts-label">pts</span>
          </div>
          <div className="player-row__points-breakdown">
            <span className="player-row__bonus">+{parseFloat(bonus).toFixed(1)} bonus</span>
          </div>
        </div>

        {/* Report card link — small icon, stops propagation so the row's
            outer Link doesn't fire underneath it. */}
        <button
          type="button"
          className="player-row__recap"
          onClick={handleRecapClick}
          aria-label={`See ${name}'s season report card`}
          title="Season report card"
        >
          <FontAwesomeIcon icon={faAward} />
        </button>
      </GlassCard>
    </Link>
  );
};

export default ResultsRow;
